import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Stethoscope, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { InvestorPicker } from './InvestorPicker';

/**
 * StatementSanityCheckPanel
 * -------------------------
 * Quick eyeball check before/after generating a statement.
 *
 * Inputs: investor + period_start + period_end.
 * Output: derived statement numbers (opening_balance, closing_balance,
 * subscriptions, redemptions) alongside the raw inputs that should
 * produce them (opening/closing cash + loan balances, sum of investments,
 * withdrawals, costs, fees, bank interest). Also shows a cash-drift
 * reconciliation so logic regressions surface immediately.
 */
export function StatementSanityCheckPanel({ API_URL, headers }) {
  const [investorId, setInvestorId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [investorsLoading, setInvestorsLoading] = useState(false);

  // Fetch selectable investors once on mount so the picker has options.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInvestorsLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/statements/selectable-investors`,
          { headers },
        );
        if (!cancelled) setInvestors(r.data?.investors || []);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Failed to load investors');
        }
      } finally {
        if (!cancelled) setInvestorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  const fmt = (n) => Number(n || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  const run = async () => {
    if (!investorId || !periodStart || !periodEnd) {
      toast.error('Pick an investor and both dates');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/statements/sanity-check`,
        {
          headers,
          params: { investor_id: investorId, period_start: periodStart, period_end: periodEnd },
        }
      );
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sanity check failed');
    } finally {
      setLoading(false);
    }
  };

  const row = (label, val, hi = false) => (
    <div className={`flex items-center justify-between py-1.5 text-sm ${hi ? 'font-semibold' : ''}`}>
      <span className="text-slate-600">{label}</span>
      <span className="font-mono">{fmt(val)}</span>
    </div>
  );

  const drift = data?.cash_reconciliation;

  return (
    <Card className="border-slate-200" data-testid="statement-sanity-check-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-emerald-600" />
          <div>
            <CardTitle className="text-base">Statement Sanity Check</CardTitle>
            <CardDescription className="text-xs">
              Eyeball the 4 derived statement numbers vs the raw inputs.
              Catches logic regressions without opening the PDF.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Investor</Label>
            <InvestorPicker
              value={investorId}
              onChange={setInvestorId}
              investors={investors}
              includeAll={false}
              placeholder={investorsLoading ? 'Loading investors…' : 'Select investor…'}
              disabled={investorsLoading}
              className="w-full"
              testId="sanity-investor-picker"
            />
          </div>
          <div>
            <Label className="text-xs">Period Start</Label>
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              data-testid="sanity-period-start"
            />
          </div>
          <div>
            <Label className="text-xs">Period End</Label>
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              data-testid="sanity-period-end"
            />
          </div>
        </div>
        <Button
          onClick={run}
          disabled={loading || !investorId || !periodStart || !periodEnd}
          data-testid="sanity-run-button"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Running…' : 'Run Sanity Check'}
        </Button>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t" data-testid="sanity-result">
            <div className="space-y-1">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">
                Derived (what the statement shows)
              </h4>
              {row('Opening Balance', data.derived.opening_balance, true)}
              {row('Closing Balance', data.derived.closing_balance, true)}
              {row('Subscriptions (Investments)', data.derived.subscriptions)}
              {row('Redemptions (Withdrawals)', data.derived.redemptions)}
            </div>
            <div className="space-y-1">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">
                Raw inputs (what produced those numbers)
              </h4>
              {row('Opening Cash (day before period_start)', data.raw_inputs.opening_cash_balance)}
              {row('Opening Loan Balance', data.raw_inputs.opening_loan_balance)}
              {row('Closing Cash @ period_end', data.raw_inputs.closing_cash_balance)}
              {row('Closing Loan Balance', data.raw_inputs.closing_loan_balance)}
              <div className="border-t my-2" />
              {row('Σ Investments in period', data.raw_inputs.sum_investments_in_period)}
              {row('Σ Withdrawals in period', data.raw_inputs.sum_withdrawals_in_period)}
              {row('Σ Costs in period', data.raw_inputs.sum_costs_in_period)}
              {row('Σ Mgmt Fees in period (ex-VAT)', data.raw_inputs.sum_mgmt_fees_in_period_ex_vat)}
              {row('Σ Perf Fees in period (ex-VAT)', data.raw_inputs.sum_perf_fees_in_period_ex_vat)}
              {row('Σ Bank Interest in period', data.raw_inputs.sum_bank_interest_in_period)}
            </div>
            <div className="md:col-span-2 mt-2 p-3 rounded-md border bg-slate-50" data-testid="sanity-drift">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-700 font-medium flex items-center gap-2">
                  {drift?.drift_is_clean ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  Cash Reconciliation
                </span>
                <Badge variant={drift?.drift_is_clean ? 'outline' : 'destructive'}>
                  drift {drift?.drift > 0 ? '+' : ''}{fmt(drift?.drift)}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Actual closing cash <strong>R{fmt(drift?.actual_closing_cash)}</strong> vs expected from period flows
                (opening + Σinv − Σwd − Σcosts − (Σfees ex-VAT)×1.15 + Σbank_int) =
                <strong> R{fmt(drift?.expected_closing_cash_from_period_flows)}</strong>.
                {drift?.drift_is_clean
                  ? ' ✓ Math reconciles — no missing flows.'
                  : ' ⚠ Drift suggests a missing or extra cash flow somewhere in the period.'}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
